import { ColorsManagerService } from "./api/managers/color-manager.service";
import { ColorsApiService } from "./api/services/colors-api.service";
import { AccountResolver } from "./accounts/account.resolver";
import { EffectsModule } from "@ngrx/effects";
import { HttpClientModule, HTTP_INTERCEPTORS } from "@angular/common/http";
import { APP_INITIALIZER, NgModule } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { BrowserModule } from "@angular/platform-browser";
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";
import {
    NgbActiveModal,
    NgbModal,
    NgbModule,
} from "@ng-bootstrap/ng-bootstrap";
import { DeviceDetectorModule } from "ngx-device-detector";
import { MessageService } from "primeng/components/common/messageservice";
import { DataTableModule, GrowlModule, TooltipModule } from "primeng/primeng";
import { AccountManagerService } from "./api/managers/account-manager.service";
import { ApplicationStateManagerService } from "./api/managers/application-state-manager.service";
import { ControllerManagerService } from "./api/managers/controller-manager.service";
import { DependentDeviceManagerService } from "./api/managers/dependent-device-manager.service";
import { FirmwareManagerService } from "./api/managers/firmware-manager.service";
import { GatewayManagerService } from "./api/managers/gateway-manager.service";
import { ImageManagerService } from "./api/managers/image-manager.service";
import { LocalCacheManager } from "./api/managers/local-cache.manager";
import { LocationManagerService } from "./api/managers/location-manager.service";
import { LoginAndUserManagerService } from "./api/managers/login-and-user-manager.service";
import { MailCenterManagerService } from "./api/managers/mail-center-manager.service";
import { NotificationManagerService } from "./api/managers/notification-manager.service";
import { RuntimeStateManagerService } from "./api/managers/runtime-state-manager.service";
import { SceneManagerService } from "./api/managers/scene-manager.service";
import { ThemeManagerService } from "./api/managers/theme-manager.service";
import { AccountApiService } from "./api/services/account-api.service";
import { ApiUrlService } from "./api/services/api-url.service";
import { ApplicationStateApiService } from "./api/services/application-state-api-service";
import { ControllerApiService } from "./api/services/controller-api.service";
import { DependentDeviceApiService } from "./api/services/dependent-device-api.service";
import { FirmwareApiService } from "./api/services/firmware-api.service";
import { GatewayApiService } from "./api/services/gateway-api.service";
import { ImageAssetApiService } from "./api/services/image-asset-api.service";
import { LocationApiService } from "./api/services/location-api.service";
import { LoginAndUserApiService } from "./api/services/login-and-user-api.service";
import { MailCenterApiService } from "./api/services/mail-center-api.service";
import { NotificationService } from "./api/services/notification.service";
import { ProgramApiService } from "./api/services/program-api.service";
import { SceneApiService } from "./api/services/scene-api.service";
import { ThemeApiService } from "./api/services/theme-api.service";
import { StationApiService } from "./api/services/station-api.service";
import { AppRoutingModule } from "./app-routing.module";
import { AppComponent } from "./app.component";
import { AuthModule } from "./auth/auth.module";
import { AlertsService } from "./core/alerts/alerts.service";
import { CoreModule } from "./core/core.module";
import { AnimationFrameService } from "./services/animation-frame.service";
import { BroadcastService } from "./services/broadcast.service";
import { ControllerLookupListService } from "./services/controller-lookup-list.service";
import { LoggingService } from "./services/logging.service";
import { LookupListService } from "./services/lookup-list.service";
import { MessageBoxService } from "./services/message-box.service";
import { OverlayManagerService } from "./services/overlay-manager.service";
import { UtilityService } from "./services/utility.service";
import { ContainerCollapseService } from "./shared/container-collapse/container-collapse.service";
import { MessageBoxComponent } from "./shared/message-box/message-box.component";
import { SharedModule } from "./shared/shared.module";
import { StoreModule } from "@ngrx/store";
import { reducers, metaReducers } from "./store/reducers";
import { StoreDevtoolsModule } from "@ngrx/store-devtools";
import { environment } from "./../environments/environment";
import { NgIdleKeepaliveModule } from '@ng-idle/keepalive';
// Signalr
import { ConnectionTransports, SignalRModule } from "ng2-signalr";
import { SignalRConfiguration } from "ng2-signalr";
import { LoaderInterceptor } from "./services/loader.interceptor.service";
import { APP_BASE_HREF } from "@angular/common";
import { SessionCacheManager } from './api/managers/session-cache.manager';
import { ConfigsLoaderService } from './services/configs-loader.service';
// >= v2.0.0
export function createConfig(): SignalRConfiguration {
    const c = new SignalRConfiguration();
    const urlService = new ApiUrlService();
    const apiEndPoint = urlService.getApiEndpointUrl(
        environment.defaultApiEndpoint
    );
    c.hubName = "reportnotificationhub";
    c.url = apiEndPoint;
    c.logging = false;
    //c.logging = true;
    c.executeEventsInZone = true; // optional, default is true
    c.executeErrorsInZone = false; // optional, default is false
    c.executeStatusChangeInZone = true; // optional, default is true

    return c;
}

@NgModule({
    declarations: [
        AppComponent
    ],
    entryComponents: [
        MessageBoxComponent
    ],
    imports: [
        HttpClientModule,
        FormsModule,
        BrowserModule,
        BrowserAnimationsModule,
        TooltipModule,
        SharedModule,
        CoreModule,
        AppRoutingModule,
        AuthModule,
        GrowlModule,
        DataTableModule,
        DeviceDetectorModule.forRoot(),
        NgbModule.forRoot(),
        SignalRModule.forRoot(createConfig),
        StoreModule.forRoot(reducers, {
            metaReducers,
            runtimeChecks: {
                strictStateImmutability: false,
                strictActionImmutability: false,
                strictStateSerializability: false,
                strictActionSerializability: false,
            }
        }),
        StoreDevtoolsModule.instrument({ maxAge: 25 }),
        EffectsModule.forRoot([]),
        NgIdleKeepaliveModule.forRoot()
        // !environment.production ? StoreDevtoolsModule.instrument() : [],
    ],
    providers: [
        AccountResolver,
        BroadcastService,
        OverlayManagerService,
        ApplicationStateManagerService,
        FirmwareManagerService,
        FirmwareApiService,
        HttpClientModule,
        AnimationFrameService,
        LoginAndUserManagerService,
        LoginAndUserApiService,
        MailCenterManagerService,
        MailCenterApiService,
        ApiUrlService,
        LocalCacheManager,
        SessionCacheManager,
        AccountManagerService,
        AccountApiService,
        LocationManagerService,
        SceneManagerService,
        ThemeManagerService,
        DependentDeviceManagerService,
        GatewayManagerService,
        LocationApiService,
        SceneApiService,
        ThemeApiService,
        DependentDeviceApiService,
        GatewayApiService,
        ControllerManagerService,
        ControllerApiService,
        ImageManagerService,
        ImageAssetApiService,
        LoggingService,
        LookupListService,
        ControllerLookupListService,
        UtilityService,
        MessageBoxService,
        ContainerCollapseService,
        NotificationManagerService,
        NotificationService,
        AlertsService,
        MessageService,
        ProgramApiService,
        StationApiService,
        RuntimeStateManagerService,
        NgbModal,
        NgbActiveModal,
        ApplicationStateApiService,
        ColorsManagerService,
        ColorsApiService,
        {
            provide: HTTP_INTERCEPTORS,
            useClass: LoaderInterceptor,
            multi: true
        },
        {
            provide: APP_INITIALIZER,
            useFactory: appInitializerFactory,
            deps: [ConfigsLoaderService],
            multi: true
        },
        // TODO: ENABLE / DISABLE LOGGING INTERCEPTOR
        // { provide: HTTP_INTERCEPTORS, useClass: LoggingInterceptor, multi: true }
        { provide: APP_BASE_HREF, useValue: '/' }
    ],
    bootstrap: [AppComponent],
})
export class AppModule { }
export function appInitializerFactory(configsLoaderService: ConfigsLoaderService) {
    return () => configsLoaderService.loadConfigs();
}
